import { Imembers } from 'src/Types/module/member/memberTypes';
import { Button } from '@mui/material';
import Excel from 'exceljs';
import { useState } from 'react';
import { saveAs } from 'file-saver';
import GlobalLoader from '../SuspenseLoader/GlobalLoader';
import { imgUrl } from 'src/Helpers/utils/Constant';

const ExcelGenerator = ({
  getDownloadData,
}: {
  getDownloadData: () => Promise<{ total: number; data: Imembers[] }>;
}) => {
  const [loading, setLoading] = useState(false);

  // Helper to fetch image buffer (remote member photos)
  const getImageBuffer = async (imageUrl: string) => {
    try {
      const response = await fetch(imageUrl, { cache: 'no-cache' });
      if (!response.ok)
        throw new Error(`Image fetch failed: ${response.status}`);
      const bufferArray = await response.arrayBuffer();
      return new Uint8Array(bufferArray);
    } catch (error) {
      console.error('Error fetching image:', error);
      return null;
    }
  };

  const saveExcel = async () => {
    setLoading(true);
    try {
      const data = await getDownloadData();

      // create workbook fresh per export
      const workbook = new Excel.Workbook();
      const worksheet = workbook.addWorksheet('member_list');

      // Common border style (used later for header if needed)
      const borderAll = {
        top: { style: 'thin' as const },
        left: { style: 'thin' as const },
        bottom: { style: 'thin' as const },
        right: { style: 'thin' as const },
      };

      try {
        /** ======================
         * HEADER: LOGOS
         * ====================== */
        worksheet.mergeCells('A1:G4');

        // First logo
        const image1Response = await fetch('/static/images/logo/atab-logo.png');
        const buffer1 = await image1Response.arrayBuffer();
        const logoId1 = workbook.addImage({
          buffer: buffer1,
          extension: 'png',
        });

        // Second logo
        const image2Response = await fetch('/static/images/logo/atab2.png');
        const buffer2 = await image2Response.arrayBuffer();
        const logoId2 = workbook.addImage({
          buffer: buffer2,
          extension: 'png',
        });

        // Place first logo (left)
        worksheet.addImage(logoId1, {
          tl: { col: 2.8, row: 0.4 },
          ext: { width: 120, height: 50 },
        });

        // Place second logo (right)
        worksheet.addImage(logoId2, {
          tl: { col: 3.3, row: 0.4 },
          ext: { width: 220, height: 50 },
        });

        /** ======================
         * HEADER: ADDRESS + TITLE
         * ====================== */

        const headerCell = worksheet.getCell('A4');
        headerCell.value = {
          richText: [
            {
              text: 'Sattara Center (15th Floor), 30/A, Nayapaltan, VIP Road, Dhaka-1000, Bangladesh\n                         ',
              font: {
                size: 12,
                color: { argb: 'FF000000' },
                bold: true,
                family: 3,
              },
            },
            {
              text: 'Member Directory',
              font: {
                size: 22,
                bold: true,
                color: { argb: 'FF000000' },
                underline: true,
              },
            },
          ],
        };
        headerCell.alignment = {
          vertical: 'middle',
          horizontal: 'left',
          wrapText: true,
        };
        worksheet.getRow(1).height = 60; // header row height
      } catch (error) {
        console.warn('Failed to add logos/title:', error);
      }

      // Empty row for spacing (after title)
      worksheet.addRow([]);

      /** ======================
       * TABLE SETUP (column widths)
       * ====================== */
      worksheet.columns = [
        { width: 5 }, // SL.
        { width: 18 }, // Member No.
        { width: 25 }, // Company & Address
        { width: 25 }, // Contact Person
        { width: 15 }, // Photo
        { width: 35 }, // Contact Details
        { width: 35 }, //tin number
      ];

      // This will be the header row for the table
      const tableHeaderRowIndex = (worksheet.lastRow?.number || 0) + 1;
      const headerRow = worksheet.getRow(tableHeaderRowIndex);

      headerRow.values = [
        'SL.',
        'Member No.',
        'Company & Address',
        'Contact Person',
        'Photo',
        'Contact Details',
        'Tin Number',
      ];

      headerRow.font = { bold: true, name: 'Arial', size: 11 };
      headerRow.alignment = {
        horizontal: 'center',
        vertical: 'middle',
        wrapText: true,
      };
      headerRow.height = 22;

      headerRow.eachCell((cell) => {
        cell.border = borderAll;
      });

      // Data should start from the row after the header
      const tableDataStartRowIndex = tableHeaderRowIndex + 1;

      /** ======================
       * DATA ROWS
       * ====================== */
      await Promise.all(
        data.data.map(async (item, index) => {
          const rowNumber = tableDataStartRowIndex + index;

          // Add row with multi-line cells
          const row = worksheet.addRow([
            index + 1,
            `${item.user_member_id_card_number}\n\n\n\n\n`,
            `${item.user_member_company_name}, ${item.addressline1}`,
            `${item.company_owner_name}, ${item.company_owner_designation}`,
            '', // photo
            `Tel :${item.cellphone ? item.cellphone : ''} \n` +
              `Mobile :${item.user_member_phone} \n` +
              `Email :${item.user_member_email} \n\n\n`,
            `${item.company_tin_number ? item.company_tin_number : ''}`,
          ]);

          // Company & address rich text
          row.getCell(3).value = {
            richText: [
              {
                text: `${item.user_member_company_name}\n`,
                font: { bold: true, name: 'Arial', size: 10 },
              },
              {
                text: `${item.addressline1}\n\n`,
                font: { name: 'Arial', size: 10 },
              },
            ],
          };

          // Contact person rich text
          row.getCell(4).value = {
            richText: [
              {
                text: `${item.company_owner_name}\n`,
                font: { bold: true, name: 'Arial', size: 10 },
              },
              {
                text: `${item.company_owner_designation}\n\n\n\n\n`,
                font: { name: 'Arial', size: 10 },
              },
            ],
          };

          row.height = 90; // increase row height to fit content

          // Enable wrapText for all relevant columns
          const wrapColumns = [1, 2, 3, 4, 5, 6]; // we only have 6 columns defined
          wrapColumns.forEach((colIndex) => {
            const col = worksheet.getColumn(colIndex);
            col.alignment = {
              wrapText: true,
              horizontal: 'left',
            };
          });

          // Add member photo if available
          if (item.company_owner_photo) {
            try {
              const imgBuffer = await getImageBuffer(
                imgUrl + '/' + item.company_owner_photo
              );
              if (imgBuffer) {
                const imageId = workbook.addImage({
                  buffer: imgBuffer as any,
                  extension: 'jpeg',
                });
                worksheet.addImage(imageId, {
                  tl: { col: 4, row: rowNumber - 1 },
                  ext: { width: 100, height: 120 },
                });
              }
            } catch (error) {
              console.warn('Failed to add image for:', item.company_owner_name);
            }
          }
        })
      );

      /** ======================
       * SAVE FILE
       * ====================== */
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer]),
        `member_list_${new Date().toISOString()}.xlsx`
      );
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading ? (
        <GlobalLoader />
      ) : (
        <Button
          onClick={saveExcel}
          variant='outlined'
          size='small'
          sx={{ py: 1 }}
        >
          Download Excel
        </Button>
      )}
    </>
  );
};

export default ExcelGenerator;
